import { useOffreService } from '@europrocurement/l2d-domain';
import { castIriToId } from '@europrocurement/l2d-utils';
import {
    OriginesOffresJsonldOrigineOffreRead,
    TblRelOrigineOffreOffreRead,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useDomaine, useOrigine } from '../../ParcoursFormalite/hooks';
import { OfferSchema } from '../Offer/schema';

export type offerVisibilityType = 'FULL' | 'PARTIAL';

export type Offer = {
    id: number;
    libelle: string;
    type: offerVisibilityType;
    icon: string;
    calculCombine: {
        id: number;
        category?: number;
    };
    calculMultiple: {
        id: number;
        category?: number;
    };
};

export type OfferOrigins = {
    id: number;
    origineId: number;
    offerId: number;
    default: boolean;
};

export type TiersOfferOrigins = {
    id: number;
    tiersId: number;
    origineOffreId: number;
};

const useOffer = () => {
    const { offersApi, offerOriginsApi, offerOriginTiersApi } = useOffreService();
    const domaineId = useDomaine();
    const origineId = useOrigine();

    const getOffers = (): Promise<Array<Offer>> =>
        new Promise((resolve, reject) => {
            offersApi
                .apiOffresGetCollection({ domaineId, itemsPerPage: 999 })
                .then((response) => {
                    const rows: Array<Offer> = (response.data['hydra:member'] || [])
                        .filter(
                            (item) =>
                                undefined !==
                                (item.origines || []).find(
                                    (origine) => origine.origine?.id === origineId,
                                ),
                        )
                        .map((item) => ({
                            id: item.id as number,
                            libelle: item.libelle as string,
                            type: item.visibilite as offerVisibilityType,
                            icon: item.iconeName as string,
                            calculCombine: {
                                id: item.calculCombine?.id as unknown as number,
                                category: item.calculCombine?.categorie
                                    ? castIriToId(item.calculCombine?.categorie)
                                    : undefined,
                            },
                            calculMultiple: {
                                id: item.calculMultiple?.id as unknown as number,
                                category: item.calculMultiple?.categorie
                                    ? castIriToId(item.calculMultiple?.categorie)
                                    : undefined,
                            },
                        }))
                        .sort((a, b) => a.libelle.localeCompare(b.libelle));

                    resolve(rows);
                })
                .catch(reject);
        });

    const getOfferOrigins = (): Promise<Array<OfferOrigins>> =>
        new Promise((resolve, reject) => {
            offerOriginsApi
                .apiOriginesOffresGetCollection({
                    origineId,
                    dateDebutBefore: new Date().toISOString(),
                    dateFinAfter: new Date().toISOString(),
                    itemsPerPage: 999,
                })
                .then((response) => {
                    const rows: Array<OfferOrigins> = (response.data['hydra:member'] || []).map(
                        (item) => ({
                            id: item?.id as number,
                            origineId: item?.origine?.id as number,
                            offerId: item?.offre?.id as number,
                            default: item.default as boolean,
                        }),
                    );

                    resolve(rows);
                })
                .catch(reject);
        });

    const setOfferOrigins = (
        formValues: OfferSchema,
        offreId: number,
        relOffreOrigines: Array<TblRelOrigineOffreOffreRead>,
    ): Promise<Array<OriginesOffresJsonldOrigineOffreRead | void>> => {
        const newOriginIds = formValues.origineIds.map((relOrigin) => relOrigin.value);
        const oldOriginIds = relOffreOrigines.map((relOrigin) => relOrigin?.origine?.id as number);
        const willBeRemoved = relOffreOrigines
            ?.filter(
                (relOrigineOffre) =>
                    !newOriginIds.includes(relOrigineOffre.origine?.id as unknown as number),
            )
            .map((relOrigineOffre) => relOrigineOffre.id);
        const willBeCreated = newOriginIds.filter((originId) => !oldOriginIds?.includes(originId));

        return Promise.all([
            ...willBeCreated.map((originId) =>
                offerOriginsApi.apiOriginesOffresPost({
                    originesOffresJsonldOrigineOffreCreate: {
                        default: false,
                        dateDebut: new Date().toISOString(),
                        offre: offreId as unknown as string,
                        origine: originId as unknown as string,
                    },
                    xIdSociete: 12,
                }),
            ),
            ...willBeRemoved.map((relOffreOriginId) =>
                offerOriginsApi.apiOriginesOffresIdDelete({
                    id: relOffreOriginId as unknown as string,
                    xIdSociete: 12,
                }),
            ),
        ]);
    };

    const getTiersOfferOrigins = (tiersId: number): Promise<Array<TiersOfferOrigins>> =>
        new Promise((resolve, reject) => {
            offerOriginTiersApi
                .apiOriginesOffresTiersGetCollection({
                    tiersId,
                    dateDebutBefore: new Date().toISOString(),
                    dateFinAfter: new Date().toISOString(),
                })
                .then((response) => {
                    const rows: Array<TiersOfferOrigins> = (
                        response.data['hydra:member'] || []
                    ).map((item) => ({
                        id: item?.id as number,
                        tiersId,
                        origineOffreId: item?.offreOrigine ? castIriToId(item?.offreOrigine) : 0,
                    }));

                    resolve(rows);
                })
                .catch(reject);
        });

    const createTiersOfferOrigin = (
        tiersId: number,
        offerOriginId: number,
    ): Promise<TiersOfferOrigins> =>
        new Promise((resolve, reject) => {
            offerOriginTiersApi
                .apiOriginesOffresTiersPost({
                    originesOffresTiersJsonldOrigineOffreTiersCreate: {
                        dateDebut: new Date().toISOString(),
                        offreOrigine: offerOriginId,
                        tiers: tiersId,
                    },
                })
                .then((response) => {
                    resolve({
                        id: response.data.id as number,
                        tiersId: castIriToId(response.data.tiers as unknown as string),
                        origineOffreId: castIriToId(
                            response.data.offreOrigine as unknown as string,
                        ),
                    });
                })
                .catch(reject);
        });

    const updateTiersOfferOrigin = (
        tiersOfferOriginId: number,
        tiersId: number,
        offerOriginId: number,
    ): Promise<TiersOfferOrigins> =>
        new Promise((resolve, reject) => {
            offerOriginTiersApi
                .apiOriginesOffresTiersIdPut({
                    id: tiersOfferOriginId as unknown as string,
                    originesOffresTiersJsonldOrigineOffreTiersUpdate: {
                        dateDebut: new Date().toISOString(),
                        offreOrigine: offerOriginId,
                        tiers: tiersId,
                    },
                })
                .then((response) => {
                    resolve({
                        id: response.data.id as number,
                        tiersId: castIriToId(response.data.tiers as unknown as string),
                        origineOffreId: castIriToId(
                            response.data.offreOrigine as unknown as string,
                        ),
                    });
                })
                .catch(reject);
        });

    const deleteTiersOfferOrigin = (tiersOfferOriginId: number): Promise<void> =>
        new Promise((resolve, reject) => {
            offerOriginTiersApi
                .apiOriginesOffresTiersIdDelete({
                    id: tiersOfferOriginId as unknown as string,
                })
                .then(() => resolve())
                .catch(reject);
        });

    const getOfferTypeColor = (offer: Offer) => {
        switch (offer.type) {
            case 'FULL':
                return 'primary';
            case 'PARTIAL':
                return 'danger';
            default:
                return 'default';
        }
    };

    const getOfferTypeLabel = (offer: Offer) => {
        switch (offer.type) {
            case 'FULL':
                return 'Premium';
            default:
                return 'Essentiel';
        }
    };

    return {
        getOffers,
        getOfferOrigins,
        setOfferOrigins,
        getTiersOfferOrigins,
        createTiersOfferOrigin,
        updateTiersOfferOrigin,
        deleteTiersOfferOrigin,
        getOfferTypeColor,
        getOfferTypeLabel,
    };
};

export default useOffer;
